import React from 'react'
import parse from 'html-react-parser'
import Button from '@veneer/core/dist/esm/scripts/button'
import LottieAnimation from '../pages/Instructions/LottieAnimation'

// Icons imports
import IconPrinterWarningAlt from '@veneer/core/dist/esm/scripts/icons/icon_printer_warning_alt'
import IconQRCode from '@veneer/core/dist/esm/scripts/icons/icon_qr_code'
import IconPersonAssist from '@veneer/core/dist/esm/scripts/icons/icon_person_assist'
import IconHelp from '@veneer/core/dist/esm/scripts/icons/icon_help'
import IconInfo from '@veneer/core/dist/esm/scripts/icons/icon_info'
import IconDocumentScanWithPrimaryColor from '@veneer/core/dist/esm/scripts/icons/icon_document_scan'
import IconDocumentScan from '@veneer/core/dist/esm/scripts/icons/icon_document_scan'
import IconMobileFax from '@veneer/core/dist/esm/scripts/icons/icon_mobile_fax'
import IconCloudService from '@veneer/core/dist/esm/scripts/icons/icon_cloud_service'
import IconSmartTasks from '@veneer/core/dist/esm/scripts/icons/icon_smart_tasks'
import IconInk from '@veneer/core/dist/esm/scripts/icons/icon_ink'
import IconPrinter from '@veneer/core/dist/esm/scripts/icons/icon_printer'
import IconXCircle from '@veneer/core/dist/esm/scripts/icons/icon_x_circle'
import IconWifi100 from '@veneer/core/dist/esm/scripts/icons/icon_wifi_100'
import IconTools from '@veneer/core/dist/esm/scripts/icons/icon_tools'
import IconDocumentCopy from '@veneer/core/dist/esm/scripts/icons/icon_document_copy'
import IconCaretRight from '@veneer/core/dist/esm/scripts/icons/icon_caret_right'

const iconMap = {
  icon_printer_warning_alt: <IconPrinterWarningAlt className="icon" />,
  icon_qr_code: <IconQRCode className="icon" />,
  icon_person_assist: <IconPersonAssist className="icon" />,
  icon_help: <IconHelp className="icon" />,
  icon_info: <IconInfo className="icon" />,
  icon_wifi100: <IconWifi100 className="icon" />,
  icon_x_circle: <IconXCircle className="icon" />,
  icon_document_scan: <IconDocumentScan className="icon" />,
  icon_mobile_fax: <IconMobileFax className="icon" />,
  icon_cloud_service: <IconCloudService className="icon" />,
  icon_smart_tasks: <IconSmartTasks className="icon" />,
  icon_ink: <IconInk className="icon" />,
  icon_printer: <IconPrinter className="icon" />,
  icon_tools: <IconTools className="icon" />,
  icon_caret_right_filled: <IconCaretRight className="icon" filled />,
  icon_document_copy: <IconDocumentCopy className="icon" />,
  icon_help_with_primary_color: <IconHelp color="#0073A8" className="icon" />,
  icon_document_scan_with_primary_color: (
    <IconDocumentScanWithPrimaryColor color="#0073A8" className="icon" />
  )
}

/**
 * Helper to convert PascalCase to snake_case, handling consecutive capitals
 * @param {string} str
 * @returns {string}
 * @example
 * pascalToSnake('IconPrinterWarningAlt') // 'icon_printer_warning_alt'
 * pascalToSnake('IconQRCode') // 'icon_qr_code'
 */
const pascalToSnake = (str) => {
  let result = ''
  for (let i = 0; i < str.length; i++) {
    const char = str[i]
    if (
      i > 0 &&
      /[A-Z]/.test(char) &&
      (/[a-z]/.test(str[i - 1]) ||
        (i + 1 < str.length && /[a-z]/.test(str[i + 1])))
    ) {
      result += '_'
    }
    result += char
  }
  return result.toLowerCase()
}

/**
 * Comprehensive HTML parser that handles multiple ContentStack elements:
 * - Icons: <span>[[ IconName ]]</span>
 * - Buttons: <button>[[ ButtonName ]]</button>
 * - Lottie animations: <span data-lottie="url" data-lottie-index="index"></span>
 * - Replacer tags: <element data-replacer="key"></element>
 *
 * @param {Object} params
 * @param {string} params.htmlString - The HTML string to parse
 * @param {Array<{button: string, props: object}>} [params.buttonsToParse=[]] - Array of button configurations
 * @param {Object.<string, React.ReactNode>} [params.replacements={}] - Replacer tag mappings
 * @returns {React.ReactNode|null} The parsed React nodes or null if input is invalid
 */
const renderContentStackInHtml = ({
  htmlString,
  buttonsToParse = [],
  replacements = {}
}) => {
  if (!htmlString) return null

  // NOTE: The extra escape characters are used to avoid issues with regex parsing
  // eslint-disable-next-line no-useless-escape
  const buttonNameRegex = /\[\[\s([^\]\[]+)\s\]\]/

  // Create button map from buttonsToParse
  const buttonMap = buttonsToParse.reduce((acc, { button, props }) => {
    const match = button.match(buttonNameRegex)
    if (match && match[1]) {
      acc[match[1]] = props
    }
    return acc
  }, {})

  return parse(htmlString, {
    replace: (domNode) => {
      // Handle Icons in spans with [[ IconName ]] format
      if (
        domNode.type === 'tag' &&
        domNode.name === 'span' &&
        domNode.children &&
        domNode.children.length === 1 &&
        domNode.children[0].type === 'text'
      ) {
        const text = domNode.children[0].data.trim()
        const match = text.match(/^\[\[\s*([\w]+)\s*\]\]$/)
        if (match) {
          const iconKey = pascalToSnake(match[1])
          if (iconMap[iconKey]) {
            return iconMap[iconKey] // Return directly without cloneElement
          }
        }
      }

      // Handle Buttons with [[ ButtonName ]] format
      if (
        domNode.type === 'tag' &&
        domNode.name === 'button' &&
        domNode.children &&
        domNode.children.length === 1 &&
        domNode.children[0].type === 'text'
      ) {
        const match = domNode.children[0].data.match(buttonNameRegex)
        if (match && match[1] && buttonMap[match[1]]) {
          const buttonName = match[1]
          const props = buttonMap[buttonName] || {}
          return (
            <Button key={buttonName} {...props}>
              {props.buttonName || buttonName}
            </Button>
          )
        }
      }

      // Handle Lottie animations with data-lottie attribute
      if (
        domNode.type === 'tag' &&
        domNode.name === 'span' &&
        domNode?.attribs?.['data-lottie']
      ) {
        const lottie = domNode.attribs['data-lottie']
        const lottieIndex = domNode.attribs?.['data-lottie-index']
        const className =
          domNode.attribs?.['class'] || domNode.attribs?.['className']

        return (
          <LottieAnimation
            url={lottie}
            index={lottieIndex}
            className={className}
            key={`lottie-${lottieIndex || 'default'}`}
          />
        )
      }

      // Handle Replacer tags with data-replacer attribute
      if (domNode.type === 'tag' && domNode?.attribs?.['data-replacer']) {
        const replacerId = domNode.attribs['data-replacer']
        if (Object.prototype.hasOwnProperty.call(replacements, replacerId)) {
          return replacements[replacerId]
        }
      }

      return undefined
    }
  })
}

export default renderContentStackInHtml

// Export individual functions for backward compatibility
export const renderIconsInHtml = (htmlString) => {
  return renderContentStackInHtml({ htmlString })
}

export const renderButtonsInHtml = ({ htmlString, buttonsToParse = [] }) => {
  return renderContentStackInHtml({ htmlString, buttonsToParse })
}

export const renderLottieInHtml = (htmlString) => {
  return renderContentStackInHtml({ htmlString })
}

export const renderReplacerTags = (htmlString, replacements) => {
  return renderContentStackInHtml({ htmlString, replacements })
}

export const renderFormattedText = (text) => {
  return renderContentStackInHtml({ htmlString: text }) || ''
}
